/**
 * /field/pt — a pressure-testing technician's own jobs (PT-3, api#917
 * `GET /pressure-testing/field/my-jobs`).
 *
 * THE SELECTION IS THE API'S. "Mine" is the JWT's user on the server, and the
 * window — every active job, scheduled jobs dated today or later (or undated),
 * jobs completed in the last 7 days — is computed in the TENANT's zone there.
 * This screen only sorts what arrived into four headings, in the API's order:
 * Active, Today, Upcoming, Recently completed. "Today" is `meta.today`, the
 * tenant's calendar day, never the phone's.
 *
 * TESTS DONE is pass + fail out of total, from the API's `test_counts`. The API
 * returns the three tallies and not their sum; adding two of its figures is
 * not a second answer to a question it already answers. Absent counts read
 * `—`, never `0 of 0`.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin } from 'lucide-react';
import { useMyPtJobs } from '../../../hooks/usePressureTesting';
import { calendarDayString, formatCalendarDate } from '../../../lib/calendar-date';
import { ptJobStatusSkin } from '../../../lib/pressure-testing';
import type { PtJob } from '../../../types/pressure-testing';
import { PtFieldBadge, PtFieldCard, PtFieldEmpty, PtFieldPage, PtFieldQueryState, PtFieldSectionTitle } from './PtFieldBits';

export function ptTestsDone(counts: PtJob['test_counts']): string {
  if (!counts || typeof counts.total !== 'number' || typeof counts.pass !== 'number' || typeof counts.fail !== 'number') return '—';
  if (counts.total === 0) return 'No tests yet';
  return `${counts.pass + counts.fail} of ${counts.total} tests done`;
}

/** Location, then LSD — whichever the job has. Neither → `—`. */
export function ptJobWhere(job: Pick<PtJob, 'location_name' | 'lsd' | 'uwi'>): string {
  const parts = [job.location_name, job.lsd ? `LSD ${job.lsd}` : null].filter(Boolean);
  if (parts.length) return parts.join(' · ');
  return job.uwi ? `UWI ${job.uwi}` : '—';
}

interface Group {
  key: 'active' | 'today' | 'upcoming' | 'completed';
  title: string;
  jobs: PtJob[];
}

/** Split the API's list into the four headings, keeping its order inside each. */
export function groupMyJobs(jobs: PtJob[], today: string | null): Group[] {
  const groups: Group[] = [
    { key: 'active', title: 'Active', jobs: [] },
    { key: 'today', title: 'Today', jobs: [] },
    { key: 'upcoming', title: 'Upcoming', jobs: [] },
    { key: 'completed', title: 'Recently completed', jobs: [] },
  ];
  const at = (k: Group['key']) => groups.find((g) => g.key === k)!.jobs;
  for (const j of jobs) {
    if (j.status === 'active') at('active').push(j);
    else if (j.status === 'scheduled') {
      // Without the tenant's day the screen cannot say which is today; both go under Upcoming.
      if (today && calendarDayString(j.scheduled_date) === today) at('today').push(j);
      else at('upcoming').push(j);
    } else if (j.status === 'complete') at('completed').push(j);
    // Anything else (a cancelled row, an unknown status) is not in the API's window; nothing is guessed for it.
  }
  return groups.filter((g) => g.jobs.length > 0);
}

function JobCard({ job }: { job: PtJob }) {
  return (
    <Link to={`/field/pt/jobs/${job.id}`} className="block" data-testid="pt-my-job">
      <PtFieldCard className="flex min-h-[88px] items-center gap-3 px-4 py-4 active:bg-fieldiq-dark/50">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-base font-extrabold text-fieldiq-text">{job.job_number || '—'}</span>
            <PtFieldBadge skin={ptJobStatusSkin(job.status)} />
          </div>
          <p className="mt-1 truncate text-base font-bold text-white">{job.customer_name || '—'}</p>
          <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-fieldiq-text">
            <MapPin className="size-3.5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
            {ptJobWhere(job)}
          </p>
          <p className="mt-1 text-xs text-fieldiq-muted">
            {ptTestsDone(job.test_counts)}
            {job.status === 'scheduled' && <> · {formatCalendarDate(job.scheduled_date, { weekday: 'short', month: 'short', day: 'numeric' }) ?? 'Not dated'}</>}
          </p>
        </div>
        <ChevronRight className="size-6 shrink-0 text-fieldiq-muted" aria-hidden="true" />
      </PtFieldCard>
    </Link>
  );
}

export default function PtMyJobsScreen() {
  const query = useMyPtJobs();
  const groups = groupMyJobs(query.data?.data ?? [], query.data?.meta.today ?? null);

  return (
    <PtFieldPage title="My PT Jobs" backTo="/field" testId="pt-my-jobs">
      <PtFieldQueryState query={query} subject="your pressure-test jobs">
        {groups.length === 0 ? (
          <PtFieldEmpty>No pressure-test jobs are assigned to you — nothing active, nothing scheduled from today, and none completed in the last 7 days. The office assigns jobs.</PtFieldEmpty>
        ) : (
          groups.map((g) => (
            <section key={g.key} aria-labelledby={`pt-group-${g.key}`}>
              <PtFieldSectionTitle>
                <span id={`pt-group-${g.key}`}>
                  {g.title} · {g.jobs.length}
                </span>
              </PtFieldSectionTitle>
              <div className="space-y-2">
                {g.jobs.map((j) => (
                  <JobCard key={j.id} job={j} />
                ))}
              </div>
            </section>
          ))
        )}
      </PtFieldQueryState>
    </PtFieldPage>
  );
}
